// Angular
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Material
import { MatDialog } from '@angular/material/dialog';
// NGRX
import { Store } from '@ngrx/store';
// CRUD
// Services and Models
import { TranslateService } from '@ngx-translate/core';
import { AppState } from 'src/app/core/reducers';
import { LayoutUtilsService, MessageType } from 'src/app/core/_base/crud';
import { ContactModel } from 'src/app/core/lists';
import { TimelineService } from 'src/app/core/timeline/_services/timeline.service';
import { FormControl, FormGroup } from '@angular/forms';
import { MeetingViewDialogComponent } from '../../../../../../pages/apps/timeline/meetings/meeting-view/meeting-view-dialog.component';
import { ViewCommunicationComponent } from '../../../../../../pages/apps/timeline/view-communication/view-communication.component';
import { DatePipe } from '@angular/common';
import { FilteredViewService } from '../../../../../../../core/services/filtered-view.service';
import { UserService } from '../../../../../../../core/auth/_services';
import { ContextualHistoryService } from '../../services/contextual-history.service';
import { take } from 'rxjs/operators';
import { CommunicationsService } from '../../../../../../../core/timeline/_services';

@Component({
  selector: 'kt-contact-timeline',
  templateUrl: './contact-timeline.component.html',
  styleUrls: ['./contact-timeline.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactTimelineComponent implements OnInit, OnDestroy {
  // Subscriptions
  timelines: any[] = [];
  contactId: number;
  private subscriptions: any[] = [];
  contact: ContactModel;
  sentFilter = '';
  search = new FormGroup({
    searchInput: new FormControl(null),
  });
  filter = new FormGroup({
    timelineFilter: new FormControl({ img: '', name: '', label: 'All' }),
  });
  spinnerStateSent = true;
  private fromDate: string;
  private toDate: string;
  @Input() contactIdFromFilteredView: any;
  @Input() filteredView;
  showReviews = false;
  sessionsFilter = [];
  apps = [];

  constructor(
    public dialog: MatDialog,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private layoutUtilsService: LayoutUtilsService,
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private timeline: TimelineService,
    private userService: UserService,
    private filterViewService: FilteredViewService,
    private contextualHistoryService: ContextualHistoryService,
    private communicationsService: CommunicationsService,
    public datepipe: DatePipe,
  ) {}

  ngOnInit() {
    this.communicationsService.getApps().subscribe((res) => {
      this.apps = res;
    });
    this.timeline.loadTimeline$.subscribe((e) => {
      this.showReviews = false;
      this.sessionsFilter = [];
      if (this.contactId && e) {
        this.timelines = [];
        this.loadTimelines();
      }
    });
    this.refreshDate();

    this.subscriptions.push(
      this.contextualHistoryService.contactIdSub.subscribe((id) => {
        this.contactId = id;
        this.search.get('searchInput').patchValue('');
        this.filter.get('timelineFilter').patchValue({ img: '', name: '', label: 'All' });

        if (this.contactId) this.loadTimelines();
      }),
    );
  }

  loadTimelines(type = '') {
    this.spinnerStateSent = true;
    this.sentFilter = type;
    const term = this.search.get('searchInput').value;
    this.sessionsFilter = this.showReviews
      ? ['OB_CALL', 'IB_CALL', 'REVIEW']
      : ['OB_CALL', 'IB_CALL'];
    this.timeline
      .findSentTimelines(
        term,
        type,
        this.fromDate,
        this.toDate,
        this.sessionsFilter,
        this.contactId,
      )
      .pipe(take(1))
      .subscribe((res) => {
        this.timelines = this.timelines.concat(res.data);
        this.spinnerStateSent = false;
        this.cdr.detectChanges();
      });
  }

  resetSent() {
    this.refreshDate();
    this.timelines = [];
  }

  searchAction() {
    this.resetSent();
    this.loadTimelines(this.sentFilter);
  }

  getItem(type: string): string {
    switch (type) {
      case 'FAX':
        return './assets/media/svg/icons/Communication/Incoming-box.svg';
      case 'MEETING':
        return './assets/media/svg/icons/Code/Time-schedule.svg';
      case 'EMAIL':
        return './assets/media/svg/icons/Communication/Incoming-mail.svg';
      case 'NOTE':
        return './assets/media/svg/icons/General/Notifications1.svg';
      case 'FILTERED_VIEW_SESSION':
        return './assets/media/svg/icons/General/focus-svgrepo-com.svg';
      case 'NOTIFICATION':
        return './assets/media/svg/icons/General/notification2.svg';
      case 'ALERT':
        return './assets/media/svg/icons/Code/Warning-2.svg';
    }
  }

  getItemClass(item): string {
    if (!item.received_id) {
      return 'timeline-item timeline-item-left ';
    }
    return 'timeline-item timeline-item-right ';
  }

  readMore(item, type) {
    item.contactId = this.contactId;
    // tslint:disable-next-line:prefer-const
    let id;
    if (item.communication_id) {
      id = item.communication_id;
      item.id = item.communication_id;
    } else {
      id = item.received_id;
      item.id = item.received_id;
    }
    switch (type) {
      case 'FAX': {
        const faxDialogRef = this.dialog.open(ViewCommunicationComponent, { data: item });
        faxDialogRef.afterClosed().subscribe((res) => {
          if (!res) {
            return;
          }
        });
        return;
      }
      case 'MEETING': {
        const _saveMessage = this.translate.instant('DIALOG.SAVE');
        const _messageType = id ? MessageType.Update : MessageType.Create;
        const dialogRef = this.dialog.open(MeetingViewDialogComponent, { data: { meeting: item } });
        dialogRef.afterClosed().subscribe((res) => {
          if (!res) {
            return;
          }
          this.layoutUtilsService.showActionNotification(
            _saveMessage,
            _messageType,
            5000,
            false,
            false,
          );
        });
        return;
      }
      case 'EMAIL':
      case 'ALERT':
      case 'NOTIFICATION': {
        const emailDialogRef = this.dialog.open(ViewCommunicationComponent, { data: item });
        emailDialogRef.afterClosed().subscribe((res) => {
          if (!res) {
            return;
          }
        });
        return;
      }
      case 'FILTERED_VIEW_SESSION': {
        let duration: number | null = null;
        if (item.from_time && item.to_time) {
          const from = new Date(item.from_time);
          const to = new Date(item.to_time);
          duration = Math.round((((to.getTime() - from.getTime()) % 86400000) % 3600000) / 60000);
        }
        this.filterViewService.getSessionById(item.communication_id).subscribe((response) => {
          if (response && response.id) {
            const notDialogRef = this.dialog.open(ViewCommunicationComponent, {
              data: {
                session: response,
                type: 'session',
                duration,
                fromDate: item.from_time,
                toDate: item.to_time,
                userName: item.sender_name,
              },
              width: '900px',
            });
            notDialogRef.afterClosed().subscribe((res) => {
              if (!res) {
                return;
              }
            });
          }
        });
        return;
      }
    }
  }

  ngOnDestroy() {
    this.subscriptions.forEach((el) => {
      if (el.sub) {
        el.sub.unsubscribe();
      }
    });
    this.showReviews = false;
  }

  getItemColor(type: any) {
    const main = 'svg-icon svg-icon-';
    switch (type) {
      case 'FAX':
        return main + 'success';
      case 'MEETING':
        return main + 'danger';
      case 'EMAIL':
        return main + 'primary';
      case 'NOTE':
        return main + 'warning';
      case 'FILTERED_VIEW_SESSION':
        return main + 'secondary';
      case 'NOTIFICATION':
      case 'ALERT':
        return main + 'info';
    }
  }

  refreshDate() {
    const date = new Date();
    // tslint:disable-next-line:variable-name
    this.fromDate = this.datepipe.transform(date, 'yyyy-MM-dd');
    date.setDate(date.getDate() - 360);
    this.toDate = this.datepipe.transform(date, 'yyyy-MM-dd');
  }

  getDate(created: any) {
    return this.datepipe.transform(created, 'yyyy-MM-dd HH:mm', 'UTC');
  }

  getRealType(type) {
    switch (type) {
      case 'FAX':
        return 'FAX';
      case 'MEETING':
        return 'Meeting';
      case 'EMAIL':
        return 'Email';
      case 'FILTERED_VIEW_SESSION':
        return 'Session';
      case 'NOTIFICATION':
        return 'Mobile Notification';
      case 'ALERT':
        return 'Mobile Alert';
    }
  }

  getApplicationName(application_id) {
    const selectedApp = this.apps.filter((app) => app.id === application_id);
    if (selectedApp) return selectedApp[0]?.name;
  }
}
